/**
 * mv_mobile_sheet_v1 A/B test (started 2026-10-24): does a slim bar at the top
 * of the music video page bring more phone visitors through sign-up than the
 * "Solmi is better on the app" bottom sheet, and what does it cost in installs?
 *
 * Oct 21 2026 read (PostHog): signed-out visitors on /ai-music-video-generator
 * sign up at 45.0% on phones against 53.8% on desktop. The sheet
 * (components/MobileAppPrompt.tsx) is 248 of 812 px (31%), covers the page's
 * "Make music video" button on arrival and shows on 91% of mobile visits.
 * What people tap: "Open" 19% (web sign-up 14–24%), nothing 14% (9–13%),
 * "Continue on web" 58% (56–68%). "Open" is also the only way a phone visitor
 * gets the app from here: ~4,270 taps a week site-wide, ~1,436 Android
 * installs attributed solmi_web/mobile_prompt (iOS can't be attributed). The
 * sheet is a trade, not a pure leak, so the page gets a test instead of a fix.
 *
 * "sheet" (control) is today's bottom sheet. "banner" is one line at the top
 * of the page (app icon, the sheet's title, Open, ×) in the page's flow, so it
 * pushes the page's own header down instead of covering it, and scrolls away
 * with it. × is the sheet's "Continue" (same dismissal key); Open carries the
 * sheet's attribution, so Android installs stay solmi_web/mobile_prompt.
 *
 * Who is in the test: a phone visitor the prompt would show to on the music
 * video page or one of its DirectorHome aliases (MV_MOBILE_SHEET_PATHS). Every
 * other page shows the sheet, with no arm on its events. Someone who already
 * dismissed the prompt never sees it there and is never drawn.
 *
 * The browser is the unit: the arm is drawn once, the first time the prompt
 * would show on those pages, and kept in localStorage, so a visitor keeps it
 * through sign-up. A caller that knows the user id draws from a salted FNV-1a
 * bucket of it instead (independent of the other live tests); the prompt
 * renders before auth.me resolves, so it doesn't pass one.
 *
 * Kill switch: VITE_MV_MOBILE_SHEET_BANNER_PERCENT=0 (build-time, default 50)
 * shows everyone the sheet, draws nothing and stamps nothing. Other values
 * only decide new draws; stored arms stay.
 *
 * Readout: app_prompt_shown and app_prompt_click carry `experiment` and `arm`
 * on those pages (keep $viewport_width < 768: the prompt reports on tablets,
 * where md:hidden keeps it off screen). The arm is also the person property
 * mv_mobile_sheet_v1_arm, set once: on the exposure event, and again when the
 * visitor signs up or in (useAuth). PostHog keeps no person profile for an
 * anonymous visitor (identified_only) and drops the first on their events, so
 * the first alone misses most visitors who sign up later (home_style_tiles_v1,
 * Oct 10–24: 92% of those exposed before sign-up had no arm on their
 * sign_up). Then sign_up and purchase by arm; Android installs join
 * install_attributed (utm_content mobile_prompt) to the click on web_click_id.
 */

import { fnv1a32 } from "./directorStyleBoost";

export const MV_MOBILE_SHEET_EXPERIMENT = "mv_mobile_sheet_v1";

export type MvMobileSheetArm = "sheet" | "banner";

export const MV_MOBILE_SHEET_STORAGE_KEY = `solmi:exp:${MV_MOBILE_SHEET_EXPERIMENT}`;

/** The person property carrying the arm. */
export const MV_MOBILE_SHEET_PERSON_PROPERTY = `${MV_MOBILE_SHEET_EXPERIMENT}_arm`;

/**
 * The tested pages, without a language prefix: the music video generator and
 * every other route App.tsx serves with DirectorHome. The dance pages and a
 * project's own page are not in the test.
 */
export const MV_MOBILE_SHEET_PATHS: readonly string[] = [
  "/ai-music-video-generator",
  "/free-ai-music-video-generator",
  "/ai-rap-video-generator",
  "/suno-to-video",
  "/ai-movie-maker",
  "/ai-music-visualizer",
  "/ai-reel-maker",
  "/ai-music-video-director",
  "/ai-music-video-workflow",
  "/how-to-make-ai-music-video",
];

/**
 * True for a tested page. Takes the path without its language prefix
 * (useLanguageRouter's pathWithoutLang). A query string, a hash, a trailing
 * slash and letter case don't matter, as they don't to the router.
 */
export function isMvMobileSheetPage(pathWithoutLang: string): boolean {
  const path = (pathWithoutLang.split(/[?#]/)[0] || "/").toLowerCase();
  return MV_MOBILE_SHEET_PATHS.includes(
    path.length > 1 ? path.replace(/\/+$/, "") : path,
  );
}

export function mvMobileSheetBannerPercent(raw: unknown): number {
  if (raw === undefined || raw === null || raw === "") return 50;
  const value = Number(raw);
  if (!Number.isFinite(value)) return 50;
  return Math.min(100, Math.max(0, Math.round(value)));
}

export function mvMobileSheetBucket(seed: string): number {
  return fnv1a32(`${MV_MOBILE_SHEET_EXPERIMENT}:${seed}`) % 100;
}

function isArm(value: unknown): value is MvMobileSheetArm {
  return value === "sheet" || value === "banner";
}

type ArmStorage = Pick<Storage, "getItem" | "setItem">;

function browserStorage(): ArmStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    // Reading window.localStorage itself throws when site data is blocked.
    return null;
  }
}

function readStoredArm(storage: ArmStorage | null): MvMobileSheetArm | null {
  try {
    const stored = storage?.getItem(MV_MOBILE_SHEET_STORAGE_KEY);
    return isArm(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * This browser's arm, or null when the test is off. Reads the stored arm
 * first; otherwise draws one (from the user id when given, else at random)
 * and stores it. Storage that throws still yields an arm, just not a sticky
 * one. `storage` defaults to localStorage.
 */
export function mvMobileSheetArm(input: {
  userId?: string | number | null;
  percent: number;
  storage?: ArmStorage | null;
  random?: () => number;
}): MvMobileSheetArm | null {
  if (input.percent <= 0) return null;
  const storage =
    input.storage === undefined ? browserStorage() : input.storage;
  const stored = readStoredArm(storage);
  if (stored) return stored;
  const userId =
    input.userId === null || input.userId === undefined
      ? ""
      : String(input.userId);
  const bucket = userId
    ? mvMobileSheetBucket(userId)
    : Math.floor((input.random ?? Math.random)() * 100);
  const arm: MvMobileSheetArm = bucket < input.percent ? "banner" : "sheet";
  try {
    storage?.setItem(MV_MOBILE_SHEET_STORAGE_KEY, arm);
  } catch {}
  return arm;
}

let pageLoadArm: MvMobileSheetArm | null | undefined;

/**
 * The arm for this page load: drawn on the first ask and reused, so the
 * prompt never changes form under a visitor, even where localStorage is
 * blocked and every draw would be a fresh one.
 */
export function mvMobileSheetArmForPageLoad(
  input: Parameters<typeof mvMobileSheetArm>[0],
): MvMobileSheetArm | null {
  if (pageLoadArm === undefined) pageLoadArm = mvMobileSheetArm(input);
  return pageLoadArm;
}

/**
 * The arm the prompt uses on this render: null while it is hidden, on every
 * page outside the test and with the test off, so those show the sheet and
 * report no arm. The first render that shows it on a tested page draws.
 */
export function mvMobileSheetArmForPrompt(input: {
  path: string;
  visible: boolean;
  percent: number;
}): MvMobileSheetArm | null {
  if (!input.visible || !isMvMobileSheetPage(input.path)) return null;
  return mvMobileSheetArmForPageLoad({ percent: input.percent });
}

/** What app_prompt_shown and app_prompt_click add on a tested page. */
export function mvMobileSheetEventProps(
  arm: MvMobileSheetArm | null,
): Record<string, string> {
  return arm ? { experiment: MV_MOBILE_SHEET_EXPERIMENT, arm } : {};
}

/** The person property to set once, or undefined outside the test. */
export function mvMobileSheetPersonProps(
  arm: MvMobileSheetArm | null,
): Record<string, string> | undefined {
  return arm ? { [MV_MOBILE_SHEET_PERSON_PROPERTY]: arm } : undefined;
}

/**
 * The person property to set once when a visitor signs up or in (useAuth's
 * identify call): the arm this browser already holds, undefined if none or
 * with the test off. It never draws, which would put every desktop sign-up
 * into the test.
 */
export function mvMobileSheetIdentifyProps(
  rawPercent: unknown,
  storage?: ArmStorage | null,
): Record<string, string> | undefined {
  if (mvMobileSheetBannerPercent(rawPercent) <= 0) return undefined;
  return mvMobileSheetPersonProps(
    readStoredArm(storage === undefined ? browserStorage() : storage),
  );
}
